import { DateTime } from "luxon";
import { create } from "zustand";

import { ServiceOrderType } from "@/_constants/serviceOrder.ts";
import { safeToDateTime } from "@/_utils/safeDateTime";
import { BusinessServiceType } from "@/common/enums/sharedEnums";

import { IDwelling } from "../../dwelling/reducers";
import {
  IDiagnosticServiceOrdering,
  IErpExpressServiceOrdering,
  IInventoryServiceOrdering,
  IKeyServiceOrdering,
  IPreviousTenantServiceOrdering,
  IServiceOrdering,
  ITenantServiceOrdering,
} from "../interfaces";

export type TServiceInventoryStoreState = {
  currentForm?: ServiceOrderType;
  businessServiceType?: BusinessServiceType;
  dwelling?: IDwelling;
  key?: IKeyServiceOrdering;
  state?: Record<"value" | "label", string>;
  previousTenant?: IPreviousTenantServiceOrdering;
  incomingTenants?: Array<ITenantServiceOrdering>;
  outgoingTenants?: Array<ITenantServiceOrdering>;
  currentTenants?: Array<ITenantServiceOrdering>;
  inventory?: IInventoryServiceOrdering;
  diagnostic?: IDiagnosticServiceOrdering;
  pppt?: IDiagnosticServiceOrdering;
  condominiumVisit?: IDiagnosticServiceOrdering;
  condominiumCreation?: IDiagnosticServiceOrdering;
  energyAudit?: IDiagnosticServiceOrdering;
  erpExpress?: IErpExpressServiceOrdering;
  tenantLeaseEndDate?: string;
  tenantLeaseStartDate?: string;
  isManagedByMainGroup: boolean;
  isConfirmed: boolean;
  operator?: Record<"value" | "label", string>;
  manager?: Record<"value" | "label", string>;
};
type Actions = {
  setCurrentForm: (form: ServiceOrderType) => void;
  setBusinessServiceType: (type: BusinessServiceType) => void;
  resetBusinessType: () => void;
  setData: (data: TServiceInventoryStoreState) => void;
  getData: () => IServiceOrdering;
  getStartDate: (
    type: ServiceOrderType,
    appointmentStartDate?: string,
    tenantLeaseEndDate?: string,
    tenantLeaseStartDate?: string
  ) => string;
  removeElement: (element: string) => void;
  reset: () => void;
  setDwelling: (dwelling: IDwelling) => void;
  setShowModalDwellingExpress: (value: boolean) => void;
  setDwellingExpressAddress: (value: string) => void;
  setAddressFilter: (value: string) => void;
  setSelectedAddress: (dwelling?: IDwelling) => void;
};

type Modals = {
  showModalDwellingExpress: boolean;
  dwellingExpressAddress: string;
};
type addressFilter = {
  address: string;
  selectedAddress?: IDwelling;
};

const initialState: TServiceInventoryStoreState & Modals & addressFilter = {
  currentForm: ServiceOrderType.OUTGOING_INVENTORY,
  dwelling: undefined,
  key: undefined,
  state: undefined,
  previousTenant: undefined,
  incomingTenants: undefined,
  outgoingTenants: undefined,
  currentTenants: undefined,
  inventory: undefined,
  pppt: undefined,
  businessServiceType: undefined,
  condominiumVisit: undefined,
  condominiumCreation: undefined,
  energyAudit: undefined,
  diagnostic: undefined,
  erpExpress: undefined,
  showModalDwellingExpress: false,
  dwellingExpressAddress: "",
  isManagedByMainGroup: true,
  isConfirmed: false,
  operator: undefined,
  address: "",
  selectedAddress: undefined,
};

type TUseServiceInventoryStore = TServiceInventoryStoreState &
  Actions &
  Modals &
  addressFilter;

export const useServiceInventoryStore = create<TUseServiceInventoryStore>(
  (set, get) => ({
    ...initialState,
    setShowModalDwellingExpress: (value) =>
      set((state) => ({ ...state, showModalDwellingExpress: value })),
    setBusinessServiceType: (type) => set({ businessServiceType: type }),
    resetBusinessType: () => set({ businessServiceType: undefined }),
    setCurrentForm: (form) => set({ currentForm: form }),
    setDwelling: (dwelling) => set((state) => ({ ...state, dwelling })),
    setDwellingExpressAddress: (value) =>
      set((state) => ({ ...state, dwellingExpressAddress: value })),
    setData: (data) => {
      return set(() => {
        // Preserve user-proposed appointment dates before processing
        const userProposedAppointmentDate = data?.inventory?.appointment?.startDate;
        const hasUserProposedDate = userProposedAppointmentDate && 
          typeof userProposedAppointmentDate === 'string' &&
          userProposedAppointmentDate.includes('T'); // ISO format check

        if (data?.outgoingTenants) {
          data.currentTenants = data.outgoingTenants;
        }
        if (data?.currentTenants) {
          data.outgoingTenants = data.currentTenants;
        }
        if (data.diagnostic && !data.diagnostic.isOccupied) {
          data.currentTenants = [];
          data.outgoingTenants = [];
          data.incomingTenants = [];
        }
        if (data?.pppt && !data.pppt.isOccupied) {
          data.currentTenants = [];
          data.outgoingTenants = [];
          data.incomingTenants = [];
        }
        if (data?.condominiumVisit && !data.condominiumVisit.isOccupied) {
          data.currentTenants = [];
          data.outgoingTenants = [];
          data.incomingTenants = [];
        }
        if (data?.condominiumCreation && !data.condominiumCreation.isOccupied) {
          data.currentTenants = [];
          data.outgoingTenants = [];
          data.incomingTenants = [];
        }
        if (data?.energyAudit && !data.energyAudit.isOccupied) {
          data.currentTenants = [];
          data.outgoingTenants = [];
          data.incomingTenants = [];
        }
        // rule key
        if (data?.key) {
          // if in agency remove data key localisation
          if (data?.key.isAgency) {
            data.key = { isAgency: true };
          }
        }
        // After all processing, restore user-proposed appointment date if it exists
        if (hasUserProposedDate && data?.inventory?.appointment) {
          data.inventory.appointment.startDate = userProposedAppointmentDate;
        }
        
        return { ...data };
      });
    },
    getData: () => {
      // mapping data to api
      const dwellingId = get().dwelling?.id;
      const {
        key,
        previousTenant,
        outgoingTenants,
        incomingTenants,
        currentTenants,
        inventory,
        diagnostic,
        pppt,
        condominiumVisit,
        condominiumCreation,
        energyAudit,
        erpExpress,
        tenantLeaseStartDate,
        tenantLeaseEndDate,
        isManagedByMainGroup,
        isConfirmed,
        operator,
        manager,
        state,
        businessServiceType,
      } = get();
      if (!dwellingId) {
        throw "Information du bien dwelling non trouvée";
      }
      const serviceOrdering: IServiceOrdering = {
        dwelling: {
          id: dwellingId,
        },
        state: state ? state.value : null,
        isManagedByMainGroup,
        isConfirmed,
        ...(operator && { operatorId: operator.value }),
        ...(manager && { managerId: manager.value }),
      };

      if (key) {
        serviceOrdering.key = key;
      }

      if (
        previousTenant &&
        previousTenant?.lastname?.length > 0 &&
        previousTenant?.firstname?.length
      ) {
        serviceOrdering.previousTenant = previousTenant;
      }

      if (inventory) {
        serviceOrdering.inventory = inventory;

        // Only set default inventory start date if user hasn't proposed their own date
        const hasUserProposedDate = inventory.appointment?.startDate && 
          inventory.appointment.startDate !== tenantLeaseEndDate && 
          inventory.appointment.startDate !== tenantLeaseStartDate;

        if (!hasUserProposedDate) {
          // Set inventory start date based on service order type
          let inventoryStartDate: string;
          
          if (
            inventory.type === ServiceOrderType.OUTGOING_INVENTORY ||
            inventory.type === ServiceOrderType.OUTGOING_INVENTORY_WITH_COSTING ||
            inventory.type === ServiceOrderType.OUTGOING_INCOMING_INVENTORY
          ) {
            inventoryStartDate = tenantLeaseEndDate || DateTime.now().toISO();
          } else if (inventory.type === ServiceOrderType.INCOMING_INVENTORY) {
            inventoryStartDate = tenantLeaseStartDate || DateTime.now().toISO();
          } else {
            inventoryStartDate = DateTime.now().toISO();
          }

          if (!inventory.appointment) {
            inventory.appointment = { startDate: inventoryStartDate };
          } else {
            inventory.appointment.startDate = inventoryStartDate;
          }
        }

        if (
          (inventory.type ===
            ServiceOrderType.OUTGOING_INVENTORY_WITH_COSTING ||
            inventory.type === ServiceOrderType.OUTGOING_INVENTORY) &&
          outgoingTenants
        ) {
          for (const tenant of outgoingTenants) {
            tenant.rentalEndDate = inventory.appointment.startDate;
            tenant.leaseEndDate = tenantLeaseEndDate;
          }
        } else if (
          inventory.type === ServiceOrderType.INCOMING_INVENTORY &&
          incomingTenants
        ) {
          for (const tenant of incomingTenants) {
            tenant.rentalStartDate = inventory.appointment.startDate;
            tenant.leaseStartDate = tenantLeaseStartDate;
          }
        } else if (
          inventory.type === ServiceOrderType.OUTGOING_INCOMING_INVENTORY &&
          incomingTenants &&
          outgoingTenants
        ) {
          for (const tenant of outgoingTenants) {
            tenant.rentalEndDate = inventory.appointment.startDate;
            tenant.leaseEndDate = tenantLeaseEndDate;
          }
          for (const tenant of incomingTenants) {
            tenant.rentalStartDate = inventory.appointment.startDate;
            tenant.leaseStartDate = tenantLeaseStartDate;
          }
        }
      }

      if (diagnostic) {
        serviceOrdering.diagnostic = {
          ...diagnostic,
          types: diagnostic.types
            .filter((type) => type.isChecked)
            .map((el) => {
              return { id: el.id, name: el.name, price: el.price };
            }),
        };
      }

      if (pppt) {
        serviceOrdering.pppt = {
          ...pppt,
          types: pppt.types
            .filter((type) => type.isChecked)
            .map((el) => {
              return { id: el.id, name: el.name, price: el.price };
            }),
        };
      }

      if (condominiumVisit) {
        serviceOrdering.condominiumVisit = {
          ...condominiumVisit,
          types: condominiumVisit.types
            .filter((type) => type.isChecked)
            .map((el) => {
              return { id: el.id, name: el.name, price: el.price };
            }),
        };
      }
      if (condominiumCreation) {
        serviceOrdering.condominiumCreation = {
          ...condominiumCreation,
          types: condominiumCreation.types
            .filter((type) => type.isChecked)
            .map((el) => {
              return { id: el.id, name: el.name, price: el.price };
            }),
        };
      }
      if (energyAudit) {
        serviceOrdering.energyAudit = {
          ...energyAudit,
          types: energyAudit.types
            .filter((type) => type.isChecked)
            .map((el) => {
              return { id: el.id, name: el.name, price: el.price };
            }),
        };
      }

      if (erpExpress) {
        serviceOrdering.erpExpress = erpExpress;
      }

      serviceOrdering.tenants = [...(incomingTenants || [])];

      if (outgoingTenants) {
        serviceOrdering.tenants = [
          ...serviceOrdering.tenants,
          ...outgoingTenants,
        ];
      }
      // case just diagnostics to avoid duplicates (currentsTenants is same outgoingTenants)
      if (!outgoingTenants && currentTenants) {
        serviceOrdering.tenants = [
          ...serviceOrdering.tenants,
          ...currentTenants,
        ];
      }

      // remove id react hook form generated by default in array tenants form
      serviceOrdering.tenants = serviceOrdering.tenants.map((tenant) => {
        if (tenant?.idDwelling) {
          tenant.id = tenant.idDwelling;
          delete tenant.idDwelling;
        } else {
          delete tenant.id;
        }
        return {
          ...tenant,
        };
      });
      serviceOrdering.businessServiceType = businessServiceType;
      return serviceOrdering;
    },
    getStartDate: (
      type: ServiceOrderType,
      appointmentStartDate?: string,
      tenantLeaseEndDate?: string,
      tenantLeaseStartDate?: string
    ) => {
      const defaultStartDate = DateTime.now().toISO();
      switch (type) {
        case ServiceOrderType.OUTGOING_INVENTORY:
        case ServiceOrderType.OUTGOING_INVENTORY_WITH_COSTING:
        case ServiceOrderType.OUTGOING_INCOMING_INVENTORY:
          return tenantLeaseEndDate
            ? safeToDateTime(tenantLeaseEndDate)
            : defaultStartDate;
        case ServiceOrderType.INCOMING_INVENTORY:
          return tenantLeaseStartDate
            ? safeToDateTime(tenantLeaseStartDate)
            : defaultStartDate;
        default:
          return appointmentStartDate
            ? safeToDateTime(appointmentStartDate)
            : defaultStartDate;
      }
    },
    removeElement: (element) =>
      set((state) => {
        const copy: { [key: string]: any } = { ...state };
        copy[element] = undefined;
        return { ...copy };
      }),
    reset: () => {
      set(initialState);
    },
    address: "",
    setAddressFilter: (data: any) => set({ address: data }),
    selectedAddress: undefined,
    setSelectedAddress: (data: any) => set({ selectedAddress: data }),
  })
);
